import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import moment from "moment-timezone";
import { useNavigate } from "react-router-dom";
import {
  Autocomplete,
  Badge,
  Button,
  Chip,
  IconButton,
  InputBase,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchOffIcon from "@mui/icons-material/SearchOff";

import API_BASE_URL from "../../config/api";
import { COLORS } from "../UserDashboard/dashboardConfig";
import { usePageStyles } from "../common/pageStyles";
import FilterDrawer, { FilterCard, countFilled } from "../common/FilterDrawer";

const BASE_URL = API_BASE_URL;
const FILTERS_STORAGE_KEY = "historyFilters.v1";
const PAGE_SIZE_STORAGE_KEY = "historyPageSize";
// Backend ogranicza limit do 100 wierszy na stronę.
const PAGE_SIZE_OPTIONS = [20, 50, 100];
const FILTER_DEBOUNCE_MS = 400;

const EMPTY_FILTERS = {
  nrRef: "",
  editedBy: [],
  fieldName: [],
  valueSearch: "",
  from: "",
  to: "",
};

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json",
});

const readSavedFilters = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(FILTERS_STORAGE_KEY));
    if (!saved || typeof saved !== "object") return EMPTY_FILTERS;
    const text = (value) => (typeof value === "string" ? value : "");
    const list = (value) => (Array.isArray(value) ? value : []);
    return {
      nrRef: text(saved.nrRef),
      editedBy: list(saved.editedBy),
      fieldName: list(saved.fieldName),
      valueSearch: text(saved.valueSearch),
      from: text(saved.from),
      to: text(saved.to),
    };
  } catch (e) {
    return EMPTY_FILTERS;
  }
};

const readSavedPageSize = () => {
  try {
    const value = Number(localStorage.getItem(PAGE_SIZE_STORAGE_KEY));
    return PAGE_SIZE_OPTIONS.includes(value) ? value : 50;
  } catch (e) {
    return 50;
  }
};

const useDebounced = (value, delay) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
};

// Data z bazy jest czasem lokalnym zapisanym w polu datetime (tak jak w pozostałych widokach).
const formatDate = (value) => {
  const parsed = moment.utc(value);
  return value && parsed.isValid() ? parsed.format("DD.MM.YYYY, HH:mm") : "Brak danych";
};

const formatInputDate = (value) => value.replace("T", " ");

const describeFilters = (f) => {
  const chips = [];
  if (f.nrRef.trim()) chips.push({ id: "nrRef", label: `Nr ref: ${f.nrRef.trim()}`, clear: { nrRef: "" } });
  if (f.editedBy.length > 0) {
    chips.push({ id: "editedBy", label: `Edytował: ${f.editedBy.join(", ")}`, clear: { editedBy: [] } });
  }
  if (f.fieldName.length > 0) {
    chips.push({ id: "fieldName", label: `Pole: ${f.fieldName.join(", ")}`, clear: { fieldName: [] } });
  }
  if (f.valueSearch.trim()) {
    chips.push({ id: "valueSearch", label: `Wartość: ${f.valueSearch.trim()}`, clear: { valueSearch: "" } });
  }
  if (f.from) chips.push({ id: "from", label: `Od: ${formatInputDate(f.from)}`, clear: { from: "" } });
  if (f.to) chips.push({ id: "to", label: `Do: ${formatInputDate(f.to)}`, clear: { to: "" } });
  return chips;
};

const History = () => {
  const classes = usePageStyles();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(readSavedPageSize);
  const [filters, setFilters] = useState(readSavedFilters);
  const [facets, setFacets] = useState({ editedBy: [], fieldName: [] });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const searchRef = useRef(null);
  const requestId = useRef(0);

  const debouncedFilters = useDebounced(filters, FILTER_DEBOUNCE_MS);

  useEffect(() => {
    try {
      localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
    } catch (e) {
      // brak miejsca - filtry po prostu nie zostaną zapamiętane
    }
  }, [filters]);

  useEffect(() => {
    try {
      localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(pageSize));
    } catch (e) {
      // j.w.
    }
  }, [pageSize]);

  const fetchRows = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${BASE_URL}/edit-history/search`,
        { filters: debouncedFilters, page, pageSize },
        { headers: getAuthHeaders() },
      );
      // Odpowiedź starszego zapytania (np. po szybkiej zmianie filtra) jest ignorowana.
      if (id !== requestId.current) return;
      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setTotal(Number(data?.total || 0));
      setError(null);
    } catch (err) {
      if (id !== requestId.current) return;
      console.error("Błąd pobierania historii zmian:", err);
      setError("Nie udało się pobrać historii zmian. Spróbuj odświeżyć widok.");
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [debouncedFilters, page, pageSize]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    axios
      .get(`${BASE_URL}/edit-history/facets`, { headers: getAuthHeaders() })
      .then(({ data }) =>
        setFacets({
          editedBy: Array.isArray(data?.editedBy) ? data.editedBy : [],
          fieldName: Array.isArray(data?.fieldName) ? data.fieldName : [],
        }),
      )
      .catch((err) => console.error("Błąd pobierania list filtrów historii:", err));
  }, []);

  // Skrót "/" ustawia fokus w wyszukiwarce.
  useEffect(() => {
    const onKey = (event) => {
      const tag = (event.target.tagName || "").toLowerCase();
      if (event.key === "/" && !["input", "textarea", "select"].includes(tag)) {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const updateFilters = useCallback((patch) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(0);
  }, []);

  const clearAllFilters = () => {
    setFilters(EMPTY_FILTERS);
    setPage(0);
  };

  const activeChips = useMemo(() => describeFilters(filters), [filters]);
  const activeCount = useMemo(
    () => describeFilters({ ...filters, nrRef: "" }).length,
    [filters],
  );
  const hasFilters = activeChips.length > 0;

  const openDetail = (row, event) => {
    if (!row.nrRef) return;
    const url = `/detail/${encodeURIComponent(row.nrRef)}/${encodeURIComponent(row.customId)}`;
    if (event?.button === 1 || event?.ctrlKey || event?.metaKey) {
      window.open(url, "_blank");
    } else {
      navigate(url, { state: { from: "history" } });
    }
  };

  const empty = <span className={classes.muted}>—</span>;
  const longText = (value) =>
    value || value === 0 ? (
      <Tooltip title={String(value)} enterDelay={600}>
        <span className={classes.ellipsis}>{value}</span>
      </Tooltip>
    ) : (
      empty
    );

  return (
    <div>
      <div className={classes.pageHeader}>
        <div>
          <h1 className={classes.title}>Historia zmian</h1>
          <div className={classes.subtitle}>
            <span>
              <strong style={{ color: "#212121" }}>{total}</strong>{" "}
              {hasFilters ? "zmian spełnia filtry" : "zmian w zgłoszeniach"}
            </span>
            <Tooltip title="Odśwież teraz">
              <IconButton size="small" onClick={fetchRows} aria-label="Odśwież">
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      </div>

      <div className={classes.card}>
        <div className={classes.toolbar}>
          <div className={classes.search}>
            <SearchIcon fontSize="small" />
            <InputBase
              inputRef={searchRef}
              className={classes.searchInput}
              placeholder="Szukaj po nr ref (wiele po przecinku)…"
              value={filters.nrRef}
              onChange={(e) => updateFilters({ nrRef: e.target.value })}
              inputProps={{ "aria-label": "Nr ref" }}
            />
            {filters.nrRef ? (
              <IconButton size="small" onClick={() => updateFilters({ nrRef: "" })} aria-label="Wyczyść nr ref">
                <ClearIcon fontSize="small" />
              </IconButton>
            ) : (
              <kbd className={classes.kbd}>/</kbd>
            )}
          </div>

          <Badge badgeContent={activeCount} color="primary">
            <Button
              variant={filtersOpen || activeCount > 0 ? "contained" : "outlined"}
              startIcon={<FilterListIcon />}
              onClick={() => setFiltersOpen(true)}
              disableElevation
            >
              Filtry
            </Button>
          </Badge>

          <span className={classes.toolbarGap} />
          {hasFilters && (
            <Button size="small" onClick={clearAllFilters}>
              Wyczyść filtry
            </Button>
          )}
        </div>

        {hasFilters && (
          <div className={classes.activeFilters}>
            {activeChips.map((chip) => (
              <Chip
                key={chip.id}
                size="small"
                label={chip.label}
                onDelete={() => updateFilters(chip.clear)}
                style={{ background: COLORS.primarySoft, color: COLORS.primaryDark }}
              />
            ))}
          </div>
        )}

        {error && <div className={classes.error}>{error}</div>}

        <div className={classes.tableWrap}>
          {loading && <LinearProgress className={classes.progress} />}
          <Table stickyHeader size="small" style={{ opacity: loading ? 0.6 : 1, transition: "opacity 0.15s" }}>
            <TableHead>
              <TableRow>
                <TableCell className={classes.headCell} style={{ minWidth: 130 }}>Nr Ref</TableCell>
                <TableCell className={classes.headCell} style={{ minWidth: 170 }}>Pole</TableCell>
                <TableCell className={classes.headCell} style={{ minWidth: 200 }}>Stara wartość</TableCell>
                <TableCell className={classes.headCell} style={{ minWidth: 200 }}>Nowa wartość</TableCell>
                <TableCell className={classes.headCell} style={{ minWidth: 150 }}>Edytowana przez</TableCell>
                <TableCell className={classes.headCell} style={{ minWidth: 150 }}>Edytowana dnia</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((item) => (
                <TableRow key={item.id} hover={false} className={classes.row}>
                  <TableCell style={{ whiteSpace: "nowrap" }}>
                    {item.nrRef ? (
                      <button
                        type="button"
                        className={classes.link}
                        onClick={(e) => openDetail(item, e)}
                        onAuxClick={(e) => openDetail(item, e)}
                      >
                        {item.nrRef}
                      </button>
                    ) : (
                      empty
                    )}
                  </TableCell>
                  <TableCell style={{ maxWidth: 180 }}>{longText(item.fieldName)}</TableCell>
                  <TableCell style={{ maxWidth: 220 }}>{longText(item.oldValue)}</TableCell>
                  <TableCell style={{ maxWidth: 220 }}>{longText(item.newValue)}</TableCell>
                  <TableCell>{item.editedBy || empty}</TableCell>
                  <TableCell style={{ whiteSpace: "nowrap" }}>{formatDate(item.editedAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {!loading && rows.length === 0 && !error && (
            <div className={classes.empty}>
              <SearchOffIcon style={{ fontSize: 44, color: "#bdbdbd" }} />
              <strong style={{ fontSize: 17, color: "#424242" }}>Brak zmian do wyświetlenia</strong>
              <span>
                {hasFilters ? "Żadna zmiana nie spełnia wybranych filtrów." : "Historia zmian jest pusta."}
              </span>
              {hasFilters && (
                <Button variant="outlined" onClick={clearAllFilters}>
                  Wyczyść filtry
                </Button>
              )}
            </div>
          )}
        </div>

        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={pageSize}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setPageSize(Number(e.target.value));
            setPage(0);
          }}
          labelRowsPerPage="Wierszy na stronę:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} z ${count}`}
        />
      </div>

      <FilterDrawer
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        onClear={clearAllFilters}
        activeCount={activeCount}
        total={total}
        totalLabel="Zmian"
      >
        <FilterCard title="Co i kto" count={countFilled(filters.editedBy, filters.fieldName, filters.valueSearch)}>
          <Autocomplete
            multiple
            size="small"
            options={facets.editedBy}
            value={filters.editedBy}
            onChange={(_, value) => updateFilters({ editedBy: value })}
            noOptionsText="Brak opcji"
            renderInput={(params) => <TextField {...params} label="Edytowana przez" />}
          />
          <Autocomplete
            multiple
            size="small"
            options={facets.fieldName}
            value={filters.fieldName}
            onChange={(_, value) => updateFilters({ fieldName: value })}
            noOptionsText="Brak opcji"
            renderInput={(params) => <TextField {...params} label="Pole" />}
          />
          <TextField
            label="Szukaj w wartościach"
            size="small"
            fullWidth
            autoComplete="off"
            value={filters.valueSearch}
            onChange={(e) => updateFilters({ valueSearch: e.target.value })}
            helperText="Fragment starej lub nowej wartości"
          />
        </FilterCard>
        <FilterCard title="Zakres dat" count={countFilled(filters.from, filters.to)}>
          <TextField
            label="Od (data i godzina)"
            type="datetime-local"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={filters.from}
            onChange={(e) => updateFilters({ from: e.target.value })}
          />
          <TextField
            label="Do (data i godzina)"
            type="datetime-local"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={filters.to}
            onChange={(e) => updateFilters({ to: e.target.value })}
          />
        </FilterCard>
      </FilterDrawer>
    </div>
  );
};

export default History;
